import React, { useEffect, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "@/components/icons";
import { toast } from "sonner";
import { plural } from "@/lib/plural";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const slugify = (v) =>
  (v || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const emptyForm = {
  location_id: "",
  name: "",
  type: "pand",
  status: "active",
  ownership: "huur",
  entity: "__none__",
  note: "",
  address: { street: "", postal_code: "", city: "", country: "Nederland" },
  landlord: { name: "", contact: "", email: "", phone: "" },
  water_recycling: false,
};

export default function LocationForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [entities, setEntities] = useState([]);
  const [slugEdited, setSlugEdited] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [entitiesError, setEntitiesError] = useState(false);

  useEffect(() => {
    api
      .get("/entities")
      .then(({ data }) => setEntities(data))
      .catch(() => {
        setEntitiesError(true);
        toast.error("Kon entiteiten niet laden. Je kunt nu geen entiteit kiezen.");
      });
    if (isEdit) {
      (async () => {
        try {
          const { data } = await api.get(`/locations/${id}`);
          setForm({
            ...data,
            ownership: data.ownership || "huur",
            entity: data.entity || "__none__",
            note: data.note || "",
            address: { country: "Nederland", ...(data.address || {}) },
            landlord: data.landlord || emptyForm.landlord,
            water_recycling: Boolean(data.water_recycling),
          });
        } catch (e) {
          setError("Locatie niet gevonden.");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id, isEdit]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (obj, key, value) =>
    setForm((f) => ({ ...f, [obj]: { ...f[obj], [key]: value } }));

  const onNameChange = (value) => {
    setForm((f) => {
      const next = { ...f, name: value };
      if (!isEdit && !slugEdited) next.location_id = slugify(value);
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Naam is verplicht.");
    if (!isEdit && !slugify(form.location_id)) return setError("Locatie-code is verplicht.");

    const payload = {
      name: form.name.trim(),
      type: form.type,
      status: form.status,
      ownership: form.ownership || null,
      entity: form.entity === "__none__" ? null : form.entity,
      note: form.note,
      address: form.address,
      // Always keep the landlord object as-is; the detail page hides it for
      // owned locations, so we never destroy contact data on an ownership switch.
      landlord: form.landlord,
      water_recycling: form.water_recycling,
    };

    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/locations/${id}`, payload);
        toast.success("Locatie bijgewerkt");
        // §2.2: the werkbonnen at this location follow the corrected link.
        const mee = data?.debiteur_meegegaan || [];
        if (mee.length) {
          toast.success(`${plural(mee.length, "werkbon gaat", "werkbonnen gaan")} mee naar ${mee[0].naar_naam || "geen debiteur"}`);
        }
        const opConcept = data?.debiteur_op_concept || [];
        if (opConcept.length) {
          toast.warning(`${plural(opConcept.length, "werkbon staat", "werkbonnen staan")} op een concept en gaat niet mee`);
        }
        navigate(`/locaties/${id}`);
      } else {
        const { data } = await api.post("/locations", {
          ...payload,
          location_id: slugify(form.location_id),
        });
        toast.success("Locatie aangemaakt");
        navigate(`/locaties/${data.location_id}`);
      }
    } catch (err) {
      setError(getErrorMessage(err, "Opslaan mislukt."));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Page console={{ variant: "view", title: isEdit ? "Locatie bewerken" : "Nieuwe locatie" }}>
        <div className="max-w-4xl space-y-4" data-testid="location-form-loading">
          <Card className="p-5"><Skeleton className="h-40 w-full" /></Card>
        </div>
      </Page>
    );

  return (
    <Page console={{ variant: "view", title: isEdit ? "Locatie bewerken" : "Nieuwe locatie",
      facts: [isEdit ? "wijzigingen gelden direct" : "sporen kun je hierna toevoegen"],
      actions: [{ key: "terug", label: "Terug", icon: ArrowLeft, onClick: () => navigate(-1) }] }}>
      <div className="max-w-4xl space-y-4">

      <form onSubmit={onSubmit} className="space-y-4">
        <Card className="p-5">
          <h2 className="mb-4 text-body font-semibold text-foreground">Algemeen</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="name">Naam *</Label>
              <Input
                id="name"
                data-testid="location-name-input"
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slug">Locatie-code *</Label>
              <Input
                id="slug"
                data-testid="location-slug-input"
                className="font-mono"
                value={form.location_id}
                disabled={isEdit}
                onChange={(e) => {
                  setSlugEdited(true);
                  setField("location_id", e.target.value);
                }}
                placeholder="bijv. pand-roosendaal-kantoor"
              />
              <p className="text-data text-muted-foreground">
                {isEdit ? "Code is permanent en kan niet gewijzigd worden." : "Uniek en permanent."}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setField("type", v)}>
                <SelectTrigger data-testid="location-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="truckwash">Truckwash</SelectItem>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="kantoor">Kantoor</SelectItem>
                  <SelectItem value="opslag">Opslag</SelectItem>
                  <SelectItem value="pand">Pand</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="location-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Actief</SelectItem>
                  <SelectItem value="planned">Gepland</SelectItem>
                  <SelectItem value="closed">Gesloten</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Entiteit</Label>
              <Select value={form.entity} onValueChange={(v) => setField("entity", v)} disabled={entitiesError}>
                <SelectTrigger data-testid="location-entity-select"><SelectValue placeholder="Geen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Geen</SelectItem>
                  {entities.map((en) => (
                    <SelectItem key={en.entity_id} value={en.entity_id}>{en.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {entitiesError && (
                <p className="text-data text-destructive">Entiteiten konden niet geladen worden.</p>
              )}
            </div>
            <div className="space-y-1.5 lg:col-span-2">
              <Label htmlFor="note">Notitie</Label>
              <Textarea
                id="note"
                data-testid="location-note-input"
                value={form.note}
                onChange={(e) => setField("note", e.target.value)}
              />
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-body font-semibold text-foreground">Eigendom &amp; verhuurder</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Eigendom / Huur</Label>
              <Select value={form.ownership} onValueChange={(v) => setField("ownership", v)}>
                <SelectTrigger data-testid="location-ownership-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eigendom">Eigendom</SelectItem>
                  <SelectItem value="huur">Huur</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.ownership === "huur" && (
            <>
              <Separator className="my-4" />
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ll-name">Verhuurder (naam)</Label>
                  <Input id="ll-name" data-testid="location-landlord-name-input"
                    value={form.landlord.name}
                    onChange={(e) => setNested("landlord", "name", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ll-contact">Contactpersoon</Label>
                  <Input id="ll-contact" data-testid="location-landlord-contact-input"
                    value={form.landlord.contact}
                    onChange={(e) => setNested("landlord", "contact", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ll-email">E-mail</Label>
                  <Input id="ll-email" data-testid="location-landlord-email-input"
                    value={form.landlord.email}
                    onChange={(e) => setNested("landlord", "email", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ll-phone">Telefoon</Label>
                  <Input id="ll-phone" data-testid="location-landlord-phone-input"
                    value={form.landlord.phone}
                    onChange={(e) => setNested("landlord", "phone", e.target.value)} />
                </div>
              </div>
            </>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-body font-semibold text-foreground">Adres</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-1.5 lg:col-span-2">
              <Label htmlFor="street">Straat en huisnummer</Label>
              <Input id="street" data-testid="location-street-input"
                value={form.address.street}
                onChange={(e) => setNested("address", "street", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pc">Postcode</Label>
              <Input id="pc" data-testid="location-postal-input"
                value={form.address.postal_code}
                onChange={(e) => setNested("address", "postal_code", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="city">Plaats</Label>
              <Input id="city" data-testid="location-city-input"
                value={form.address.city}
                onChange={(e) => setNested("address", "city", e.target.value)} />
            </div>
          </div>
          <Separator className="my-4" />
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="water">Waterrecycling actief</Label>
              <p className="text-data text-muted-foreground">Gerecycled water kan de verwachte levensduur van assets verkorten.</p>
            </div>
            <Switch id="water" data-testid="location-water-recycling-switch" checked={form.water_recycling} onCheckedChange={(v) => setField("water_recycling", v)} />
          </div>
        </Card>

        <Card className="p-5" data-testid="location-ruimtes-hint">
          <h2 className="mb-1 text-body font-semibold text-foreground">Ruimtes en sporen</h2>
          <p className="text-body text-muted-foreground">
            {isEdit
              ? "Ruimtes en sporen staan op de locatiepagina zelf, zodat een machine er meteen aan gehangen kan worden."
              : "De standaard ruimtes voor dit soort locatie worden meteen aangemaakt. Daarna kun je ze op de locatiepagina aanpassen."}
          </p>
        </Card>

        {error && <p className="text-body text-destructive">{error}</p>}

        <div className="flex items-center gap-2">
          <Button type="submit" data-testid="location-form-save-button" disabled={saving}>
            {saving ? "Bezig..." : "Opslaan"}
          </Button>
          <Button type="button" variant="secondary" data-testid="location-form-cancel-button" onClick={() => navigate(-1)}>
            Annuleren
          </Button>
        </div>
      </form>
      </div>
    </Page>
  );
}
